import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ArrowRight, Play } from "lucide-react";

function ytEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
  const id = m ? m[1] : null;
  return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null;
}

export default function Work() {
  const [cases, setCases] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/cases").then((r) => setCases(r.data)).catch(() => {}); }, []);

  const open = (c) => {
    const embed = ytEmbed(c.youtube_url);
    if (embed) setActive({ ...c, embed, short: /shorts\//i.test(c.youtube_url || "") });
  };

  return (
    <>
      <SEO title="Work & Clients — Trusted by Brands Worldwide" path="/work"
        description="VoiceOverKids child voices featured in international commercials, animation, games and e-learning for brands worldwide." />
      <section className="bg-brand-ink text-white">
        <div className="container-x py-14 md:py-16">
          <span className="label-eyebrow text-brand-purple">Work</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter md:text-5xl">Trusted by brands worldwide</h1>
          <p className="mt-3 max-w-2xl text-zinc-300">A selection of our child voice-over projects. Click a case to watch the video.</p>
        </div>
      </section>

      <section className="container-x section-y">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {cases.map((c) => {
            const hasVideo = !!ytEmbed(c.youtube_url);
            return (
              <button key={c.id} data-testid={`case-${c.id}`} onClick={() => open(c)}
                className="group relative overflow-hidden rounded-2xl border border-zinc-200 bg-black text-left">
                <img src={mediaUrl(c.image_url)} alt={c.title} loading="lazy"
                  className="aspect-video w-full object-cover transition group-hover:scale-105" />
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 via-black/10 to-transparent p-3">
                  <span className="font-heading text-sm font-bold text-white">{c.title}</span>
                </div>
                {hasVideo && (
                  <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-brand-violet/90 text-white opacity-90 transition group-hover:scale-110">
                    <Play size={16} className="fill-white ml-0.5" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <p className="mt-6 text-sm text-zinc-400">Brand names and footage are project references and remain the property of their respective owners.</p>

        <div className="mt-12 rounded-3xl bg-brand-paper border border-zinc-200 p-8 text-center">
          <h2 className="font-heading text-2xl font-bold">Have a project in mind?</h2>
          <Link to="/casting" className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-violet px-6 py-3 font-bold text-white transition hover:bg-brand-violetHover">Start a casting <ArrowRight size={16} /></Link>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className={`p-0 overflow-hidden bg-black border-0 ${active && active.short ? "max-w-[420px]" : "max-w-3xl"}`}>
          {active && (
            <div className={active.short ? "aspect-[9/16] w-full" : "aspect-video w-full"}>
              <iframe title={active.title} src={active.embed} className="h-full w-full" allow="autoplay; encrypted-media" allowFullScreen />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
